/**
 * CollapsibleSection Component
 * Reusable collapsible container with expand/collapse functionality
 */

import React from 'react';
import { Box, Typography, IconButton, Collapse, Paper } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  rightContent?: React.ReactNode;
  defaultPadding?: boolean;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  isExpanded,
  onToggle,
  children,
  rightContent,
  defaultPadding = true,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 4,
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f9fafb',
          },
        }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <IconButton
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              color: '#6b7280',
              p: 0.5,
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#111827',
            }}
          >
            {title}
          </Typography>
        </Box>

        {rightContent && (
          <Box onClick={(e) => e.stopPropagation()}>
            {rightContent}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Collapse in={isExpanded}>
        <Box sx={defaultPadding ? { px: 3, pb: 3 } : {}}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};
